import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// ── Design tokens — identical to CustomDietPlan.jsx ───────────────────────────
const C = {
  ink: '#14231A', green: '#1F8A3B', leaf: '#6CC24A', mint: '#EEF9EA',
  orange: '#FF7A1A', cream: '#FFF6E8', white: '#FFFFFF',
  tomato: '#E8412F', lemon: '#FFC93C',
};
const FRAUNCES = "'Fraunces', Georgia, serif";
const POPPINS  = "'Poppins', system-ui, sans-serif";

// ── AI disclaimer — required notice, shown in multiple places ─────────────────
const _DISCLAIMER_TEXT = "Important: This diet plan is generated by AI using its best knowledge. It is not advice from a dietitian or other qualified nutrition professional. Please consult a qualified professional before making any changes to your diet.";
const AiDisclaimerBox = () => (
  <div style={{ background:'#F0FDF4', border:`1px solid ${C.leaf}`, borderRadius:12,
    padding:'12px 14px', margin:'16px 0', display:'flex', gap:10, alignItems:'flex-start',
    boxSizing:'border-box' }}>
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.green}
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink:0, marginTop:2 }}>
      <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/>
    </svg>
    <p style={{ fontFamily:POPPINS, fontSize:13, lineHeight:1.55, color:C.ink, margin:0 }}>
      {_DISCLAIMER_TEXT}
    </p>
  </div>
);
const AiDisclaimerSmall = () => (
  <p style={{ fontFamily:POPPINS, fontSize:11, color:'#999', lineHeight:1.55,
    textAlign:'center', margin:'16px 0 0', padding:'0 8px' }}>
    {_DISCLAIMER_TEXT}
  </p>
);
const RESPONSIVE_CSS = `
.dp-wrapper { container-type:inline-size; container-name:dp-page; }
.dp-hero-grid { display:flex; flex-direction:column; gap:32px; }
.dp-hero-chips { display:none; }
.dp-steps { display:grid; grid-template-columns:minmax(0,1fr); gap:16px; }
.dp-food-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:16px; }
.dp-everything { display:flex; flex-direction:column; gap:32px; align-items:center; }
.dp-gym { display:flex; flex-direction:column; gap:32px; }
.dp-kitchen { display:flex; flex-direction:column; gap:32px; }
.dp-price-bullets { display:flex; flex-direction:column; gap:10px; }
@container dp-page (min-width:520px){ .dp-steps { grid-template-columns:repeat(2,minmax(0,1fr)); } }
@container dp-page (min-width:700px){ .dp-food-grid { grid-template-columns:repeat(4,minmax(0,1fr)); gap:20px; } }
@container dp-page (min-width:768px){
  .dp-hero-grid { flex-direction:row; align-items:flex-start; gap:48px; }
  .dp-hero-chips { display:flex; }
  .dp-hero-left  { width:auto; max-width:540px; }
}
@container dp-page (min-width:820px){
  .dp-everything { flex-direction:row; gap:64px; align-items:center; }
  .dp-gym { flex-direction:row; align-items:center; gap:64px; }
  .dp-kitchen { flex-direction:row-reverse; align-items:center; gap:64px; }
}
@container dp-page (min-width:900px){ .dp-steps { grid-template-columns:repeat(4,minmax(0,1fr)); gap:20px; } }
.dp-spin { animation:dpSpin 0.8s linear infinite; }
@keyframes dpSpin { to { transform:rotate(360deg); } }
`;

// ── Mobile CSS — adapted for public page (no dashboard nav) ───────────────────
// On mobile: show fixed white header (.dp-mobile-header), hide desktop TopBar.
// padding-top offsets the fixed header (no pt-20 wrapper present here unlike member area).
const PUBLIC_MOBILE_CSS = `
@media (max-width: 767px) {
  :root { --dp-header-h: 76px; }
  .dp-wrapper { padding-top: var(--dp-header-h, 76px) !important; }
  .dp-mobile-header { display: flex !important; }
  .dp-topbar-hide-mobile { display: none !important; }
}
@media (min-width: 768px) {
  .dp-mobile-header { display: none !important; }
  .dp-sticky-bar { display: none !important; }
  .dp-mobile-spacer { display: none !important; }
}
`;

// ── GreenBtn — identical to CustomDietPlan.jsx ────────────────────────────────
const GreenBtn = ({ children, onClick, disabled, style = {}, ...rest }) => (
  <button onClick={onClick} disabled={disabled}
    style={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 16,
      background: disabled ? '#94B89E' : C.green, color: C.white,
      border: 'none', borderRadius: 14, padding: '16px 32px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      boxShadow: disabled ? 'none' : '0 4px 18px rgba(31,138,59,0.30)',
      width: '100%', minHeight: 54, transition: 'background 0.15s',
      boxSizing: 'border-box', ...style }} {...rest}>
    {children}
  </button>
);

// ── TopBar — identical to CustomDietPlan.jsx ──────────────────────────────────
const TopBar = ({ scrollToPrice }) => (
  <header style={{ background: C.white, borderBottom: '1px solid #E5EFE8', padding: '12px 20px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    position: 'sticky', top: 0, zIndex: 40, boxSizing: 'border-box', maxWidth: '100%' }}>
    <img src="/diet-plan/logo-diet-plan-transparent.png" alt="Neighborhood Boxing Diet Plan"
      style={{ height: 44, maxWidth: '60vw', objectFit: 'contain' }} />
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <a href="/"
        style={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 14, color: C.ink,
          textDecoration: 'none', opacity: 0.6, whiteSpace: 'nowrap' }}>
        ← Home
      </a>
      <button onClick={scrollToPrice}
        style={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 14, background: C.green,
          color: C.white, border: 'none', borderRadius: 10, padding: '10px 20px',
          cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
          boxShadow: '0 3px 12px rgba(31,138,59,0.25)' }}>
        Get my plan
      </button>
    </div>
  </header>
);

// ── PublicMobileHeader — logo + Get Started button (no dashboard nav) ─────────
const PublicMobileHeader = ({ onGetStarted }) => (
  <div className="dp-mobile-header" style={{
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 60,
    background: C.white, boxShadow: '0 1px 6px rgba(0,0,0,0.10)',
    height: 'var(--dp-header-h, 76px)',
    alignItems: 'center', justifyContent: 'space-between',
    padding: '0 20px', boxSizing: 'border-box',
  }}>
    <img src="/diet-plan/logo-diet-plan-transparent.png"
      alt="Neighborhood Boxing Diet Plan"
      style={{ height: 52, maxWidth: '50vw', objectFit: 'contain' }} />
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <a href="/"
        style={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 13, color: C.ink,
          textDecoration: 'none', opacity: 0.6, whiteSpace: 'nowrap' }}>
        ← Home
      </a>
      <button onClick={onGetStarted}
        style={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 14,
          background: C.green, color: C.white, border: 'none', borderRadius: 10,
          padding: '10px 16px', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
          boxShadow: '0 3px 12px rgba(31,138,59,0.25)' }}>
        Get started
      </button>
    </div>
  </div>
);

// ── VideoBlock — identical to CustomDietPlan.jsx ──────────────────────────────
const VideoBlock = ({ playing, onPlay, onKey }) => (
  <div style={{ maxWidth: '100%' }}>
    <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0,
      borderRadius: 20, overflow: 'hidden', border: `3px solid ${C.leaf}`,
      boxSizing: 'border-box' }}>
      <iframe
        src="https://www.youtube-nocookie.com/embed/0Yk3BrWde7A?rel=0"
        allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
        allowFullScreen
        title="Neighborhood Boxing Club Custom Diet Plan explainer"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
      />
    </div>
    <p style={{ fontFamily: POPPINS, fontWeight: 700, fontSize: 14, color: C.ink,
      textAlign: 'center', margin: '10px 0 0' }}>▶ Watch how it works</p>
  </div>
);

// ── HeroSection — identical to CustomDietPlan.jsx ─────────────────────────────
const HeroSection = ({ videoPlaying, onVideoPlay, onVideoKey, scrollToPrice }) => (
  <section style={{ background: C.white, padding: '32px 20px 48px', maxWidth: 1180,
    margin: '0 auto', boxSizing: 'border-box', position: 'relative', overflow: 'hidden' }}>
    <div style={{ position: 'absolute', top: -80, right: -80, width: 320, height: 320,
      borderRadius: '50%', background: C.mint, filter: 'blur(80px)', opacity: 0.7, zIndex: 0, pointerEvents: 'none' }} />
    <div style={{ position: 'absolute', bottom: -60, left: -60, width: 260, height: 260,
      borderRadius: '50%', background: C.cream, filter: 'blur(70px)', opacity: 0.8, zIndex: 0, pointerEvents: 'none' }} />
    <div className="dp-hero-grid" style={{ position: 'relative', zIndex: 1 }}>
      <div className="dp-hero-left" style={{ flex: '0 0 auto' }}>
        <div style={{ display: 'inline-block', background: C.cream, borderRadius: 100, padding: '6px 16px', marginBottom: 20 }}>
          <span style={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 13, color: C.orange, textTransform: 'uppercase', letterSpacing: '0.08em' }}>NEW · CUSTOM DIET PLAN</span>
        </div>
        <h1 style={{ fontFamily: FRAUNCES, fontWeight: 900, fontSize: 'clamp(34px,8.5vw,56px)', lineHeight: 1.1, color: C.ink, margin: '0 0 20px', maxWidth: 540 }}>
          A diet plan built for{' '}<span style={{ color: C.green }}>you</span>.{' '}Not for everybody.
        </h1>
        <div className="dp-mobile-video" style={{ marginBottom: 24 }}>
          <VideoBlock playing={videoPlaying} onPlay={onVideoPlay} onKey={onVideoKey} />
        </div>
        <p style={{ fontFamily: POPPINS, fontSize: 16, lineHeight: 1.65, color: C.ink, margin: '0 0 28px', maxWidth: 480 }}>
          Answer a few quick questions and get your own plan, with a grocery list and step-by-step cooking instructions. Built by Neighborhood Boxing Club to match your training.
        </p>
        <GreenBtn onClick={scrollToPrice} style={{ maxWidth: 340 }}>Get my plan · $9.99/month</GreenBtn>
        <p style={{ fontFamily: POPPINS, fontSize: 14, color: '#4A6B55', margin: '16px 0 0', display: 'flex', flexWrap: 'wrap', gap: '8px 20px' }}>
          <span>✓ 15 quick questions</span><span>✓ Grocery list</span><span>✓ Cooking steps</span>
        </p>
      </div>
      <div style={{ flex: '1 1 auto', minWidth: 0, position: 'relative' }} className="dp-hero-desktop-video">
        <VideoBlock playing={videoPlaying} onPlay={onVideoPlay} onKey={onVideoKey} />
      </div>
    </div>
  </section>
);

// ── HowItWorks — identical to CustomDietPlan.jsx ──────────────────────────────
const STEPS_DATA = [
  { n:1, title:'Tell us about you',  body:'About 15 quick questions: age, height, weight, goals and the way you like to eat.' },
  { n:2, title:'Get your plan',      body:'A 4-week plan made just for you. No two plans are the same.' },
  { n:3, title:'Shop the list',      body:'A grocery list sorted by food section: produce, meat and fish, dairy, pantry, frozen.' },
  { n:4, title:'Cook and train',     body:'Simple cooking steps for every meal, matched with how many classes you should take.' },
];
const HowItWorks = () => (
  <section style={{ padding:'64px 20px',maxWidth:1180,margin:'0 auto',boxSizing:'border-box' }}>
    <h2 style={{ fontFamily:FRAUNCES,fontWeight:900,fontSize:'clamp(30px,6vw,44px)',color:C.ink,textAlign:'center',margin:'0 0 8px' }}>How it works</h2>
    <p style={{ fontFamily:POPPINS,fontSize:16,color:'#4A6B55',textAlign:'center',margin:'0 0 40px' }}>Four easy steps from sign-up to your first meal.</p>
    <div className="dp-steps">
      {STEPS_DATA.map(s => (
        <div key={s.n} style={{ background:C.white,border:'1px solid #DCF0E4',borderRadius:20,padding:'28px 22px',boxShadow:'0 2px 12px rgba(31,138,59,0.06)' }}>
          <div style={{ width:40,height:40,borderRadius:'50%',background:C.green,display:'flex',alignItems:'center',justifyContent:'center',marginBottom:16 }}>
            <span style={{ fontFamily:POPPINS,fontWeight:700,fontSize:18,color:C.white }}>{s.n}</span>
          </div>
          <h3 style={{ fontFamily:FRAUNCES,fontWeight:800,fontSize:20,color:C.ink,margin:'0 0 10px' }}>{s.title}</h3>
          <p style={{ fontFamily:POPPINS,fontSize:14,lineHeight:1.6,color:'#2D4A35',margin:0 }}>{s.body}</p>
        </div>
      ))}
    </div>
  </section>
);

// ── EatYourWaySection — identical to CustomDietPlan.jsx ───────────────────────
const FOOD_CARDS = [
  { src:'/diet-plan/card-keto.jpg',label:'Keto' },
  { src:'/diet-plan/card-paleo.jpg',label:'Paleo' },
  { src:'/diet-plan/card-plant-based.jpg',label:'Plant-based' },
  { src:'/diet-plan/card-high-protein.jpg',label:'High-protein' },
];
const EatYourWaySection = () => (
  <section style={{ background:C.mint,padding:'64px 20px',boxSizing:'border-box' }}>
    <div style={{ maxWidth:1180,margin:'0 auto' }}>
      <h2 style={{ fontFamily:FRAUNCES,fontWeight:900,fontSize:'clamp(28px,5.5vw,40px)',color:C.ink,textAlign:'center',margin:'0 0 8px' }}>Eat the way you like</h2>
      <p style={{ fontFamily:POPPINS,fontSize:15,color:'#4A6B55',textAlign:'center',margin:'0 0 36px' }}>Keto, paleo, plant-based, high-protein and more.</p>
      <div className="dp-food-grid">
        {FOOD_CARDS.map(c => (
          <div key={c.label} style={{ borderRadius:20,overflow:'hidden',boxShadow:'0 4px 20px rgba(0,0,0,0.10)',position:'relative' }}>
            <img src={c.src} alt={c.label} loading="lazy" style={{ width:'100%',aspectRatio:'1/1',objectFit:'cover',display:'block' }} />
            <div style={{ position:'absolute',bottom:0,left:0,right:0,padding:'32px 16px 16px',background:'linear-gradient(to top,rgba(20,35,26,0.75) 0%,transparent 100%)' }}>
              <span style={{ fontFamily:FRAUNCES,fontWeight:800,fontSize:17,color:C.white }}>{c.label}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ── EverythingSection — identical to CustomDietPlan.jsx ───────────────────────
const FEATURES = [
  { title:'Your calories and macros',    sub:'Worked out from your body and your goal.' },
  { title:'Grocery list by food section',sub:'Shop in half the time.' },
  { title:'Easy cooking steps',          sub:'No chef skills needed.' },
  { title:'One flex meal a week',        sub:'Plan your treat. Enjoy it.' },
];
const EverythingSection = () => (
  <section style={{ padding:'64px 20px',maxWidth:1180,margin:'0 auto',boxSizing:'border-box' }}>
    <h2 style={{ fontFamily:FRAUNCES,fontWeight:900,fontSize:'clamp(28px,5.5vw,40px)',color:C.ink,textAlign:'center',margin:'0 0 40px' }}>Everything in one place</h2>
    <div className="dp-everything">
      <div style={{ flex:'0 0 auto',maxWidth:340,width:'100%' }}>
        <img src="/diet-plan/phone-mockup.jpg" alt="Diet plan app on phone" loading="lazy"
          style={{ width:'100%',maxWidth:340,borderRadius:24,display:'block',margin:'0 auto',boxShadow:'0 8px 40px rgba(0,0,0,0.12)' }} />
      </div>
      <div style={{ flex:'1 1 auto',minWidth:0 }}>
        {FEATURES.map((f,i) => (
          <div key={i} style={{ display:'flex',gap:16,alignItems:'flex-start',marginBottom:i<FEATURES.length-1?28:0 }}>
            <div style={{ width:28,height:28,borderRadius:'50%',background:C.mint,border:`2px solid ${C.leaf}`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,marginTop:2 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.green} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <div>
              <p style={{ fontFamily:POPPINS,fontWeight:600,fontSize:15,color:C.ink,margin:'0 0 2px' }}>{f.title}</p>
              <p style={{ fontFamily:POPPINS,fontSize:14,color:'#4A6B55',margin:0 }}>{f.sub}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ── GymSection — identical to CustomDietPlan.jsx ──────────────────────────────
const GymSection = () => (
  <section style={{ background:C.mint,padding:'64px 20px',boxSizing:'border-box' }}>
    <div className="dp-gym" style={{ maxWidth:1180,margin:'0 auto' }}>
      <div style={{ flex:'1 1 auto',minWidth:0 }}>
        <h2 style={{ fontFamily:FRAUNCES,fontWeight:900,fontSize:'clamp(26px,5vw,38px)',color:C.ink,margin:'0 0 16px',overflowWrap:'break-word',wordBreak:'break-word' }}>Made for people who train</h2>
        <p style={{ fontFamily:POPPINS,fontSize:15,lineHeight:1.7,color:'#2D4A35',margin:0 }}>Your plan fits your life at the gym. We suggest how many classes a week match your pace, so your food and your workouts work as a team.</p>
      </div>
      <div style={{ flex:'0 0 auto',maxWidth:480,width:'100%' }}>
        <img src="/diet-plan/gloves-and-meal.jpg" alt="Boxing gloves next to a meal" loading="lazy"
          style={{ width:'100%',borderRadius:20,display:'block',boxShadow:'0 6px 30px rgba(0,0,0,0.10)' }} />
      </div>
    </div>
  </section>
);

// ── KitchenSection — identical to CustomDietPlan.jsx ─────────────────────────
const KitchenSection = () => (
  <section style={{ padding:'64px 20px',boxSizing:'border-box' }}>
    <div className="dp-kitchen" style={{ maxWidth:1180,margin:'0 auto' }}>
      <div style={{ flex:'1 1 auto',minWidth:0 }}>
        <h2 style={{ fontFamily:FRAUNCES,fontWeight:900,fontSize:'clamp(26px,5vw,38px)',color:C.ink,margin:'0 0 16px',overflowWrap:'break-word',wordBreak:'break-word' }}>Fresh, simple, doable</h2>
        <p style={{ fontFamily:POPPINS,fontSize:15,lineHeight:1.7,color:'#2D4A35',margin:0 }}>Meals you can actually make on a busy week, with a grocery list you can actually use.</p>
      </div>
      <div style={{ flex:'0 0 auto',maxWidth:480,width:'100%' }}>
        <img src="/diet-plan/kitchen-meal-prep.jpg" alt="Meal prep in the kitchen" loading="lazy"
          style={{ width:'100%',borderRadius:20,display:'block',boxShadow:'0 6px 30px rgba(0,0,0,0.10)' }} />
      </div>
    </div>
  </section>
);

// ── PublicPriceSection — same layout as PriceSection; button goes to /login ───
// Dual pricing shown: members pay $9.99, non-members pay $19.99.
const PublicPriceSection = ({ onGetStarted }) => (
  <section id="dp-price" style={{ background:C.mint,padding:'64px 20px 80px',boxSizing:'border-box' }}>
    <div style={{ maxWidth:480,margin:'0 auto' }}>
      <div style={{ background:C.white,borderRadius:24,padding:'36px 28px',boxShadow:'0 8px 40px rgba(31,138,59,0.10)',textAlign:'center' }}>
        <img src="/diet-plan/logo-diet-plan-transparent.png" alt="Neighborhood Boxing Diet Plan" loading="lazy"
          style={{ height:52,objectFit:'contain',display:'block',margin:'0 auto 24px' }} />
        <h2 style={{ fontFamily:FRAUNCES,fontWeight:900,fontSize:26,color:C.ink,margin:'0 0 20px' }}>Your plan starts today</h2>

        {/* Dual pricing display */}
        <div style={{ display:'flex',gap:0,marginBottom:24,borderRadius:14,overflow:'hidden',border:`1px solid ${C.leaf}` }}>
          <div style={{ flex:1,padding:'14px 10px',background:C.green,textAlign:'center' }}>
            <div style={{ fontFamily:FRAUNCES,fontWeight:900,fontSize:22,color:C.white,lineHeight:1 }}>$9.99</div>
            <div style={{ fontFamily:POPPINS,fontSize:11,color:'rgba(255,255,255,0.85)',marginTop:3 }}>for members</div>
          </div>
          <div style={{ flex:1,padding:'14px 10px',background:'#F5F9F6',textAlign:'center' }}>
            <div style={{ fontFamily:FRAUNCES,fontWeight:900,fontSize:22,color:'#4A6B55',lineHeight:1 }}>$19.99</div>
            <div style={{ fontFamily:POPPINS,fontSize:11,color:'#6A8B72',marginTop:3 }}>without membership</div>
          </div>
        </div>

        <div className="dp-price-bullets" style={{ textAlign:'left',marginBottom:28 }}>
          {[
            'Personal plan with grocery list and cooking steps',
            'Cancel anytime: text the gym at (614) 547-6096',
          ].map((b,i) => (
            <div key={i} style={{ display:'flex',gap:12,alignItems:'flex-start' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.green} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0,marginTop:1 }}><polyline points="20 6 9 17 4 12"/></svg>
              <span style={{ fontFamily:POPPINS,fontSize:14,color:C.ink,lineHeight:1.5 }}>{b}</span>
            </div>
          ))}
        </div>
        <GreenBtn onClick={onGetStarted} data-testid="cdp-public-get-started-btn">
          Get Started — Log in or sign up
        </GreenBtn>
        <p style={{ fontFamily:POPPINS,fontSize:12,color:'#4A6B55',margin:'12px 0 0' }}>
          Members pay $9.99/month · Non-members pay $19.99/month · Cancel anytime
        </p>
      </div>
    </div>
  </section>
);

// ── PublicStickyBar — mobile-only sticky CTA; hides when price section visible ─
const PublicStickyBar = ({ onGetStarted }) => {
  const [priceInView, setPriceInView] = useState(false);
  useEffect(() => {
    const el = document.getElementById('dp-price');
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setPriceInView(entry.isIntersecting),
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  if (priceInView) return null;
  return (
    <div className="dp-sticky-bar" style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 45,
      background: C.white, borderTop: '1px solid #E5EFE8',
      boxShadow: '0 -4px 20px rgba(0,0,0,0.08)',
      padding: '12px 16px',
      paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
      boxSizing: 'border-box',
    }}>
      <button
        onClick={onGetStarted}
        style={{
          display: 'block', width: '100%', height: 52,
          background: C.green, color: C.white, border: 'none', borderRadius: 12,
          fontFamily: POPPINS, fontWeight: 700, fontSize: 16,
          cursor: 'pointer',
          boxShadow: '0 4px 18px rgba(31,138,59,0.30)',
          letterSpacing: '0.01em',
        }}
        data-testid="cdp-public-sticky-btn"
      >
        Get Started — Log in or sign up
      </button>
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────
const SITE = 'https://neighborhoodboxingclub.us';

export default function CustomDietPlanPage() {
  const navigate = useNavigate();
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [faqOpenIdx,   setFaqOpenIdx]   = useState(null);

  const goGetStarted = () => {
    // Ensure login and registration both redirect back to the diet plan page
    sessionStorage.setItem("nbc_redirect_after_login", "/dashboard/diet-plan");
    navigate('/login');
  };
  const scrollToPrice = () => document.getElementById('dp-price')?.scrollIntoView({ behavior: 'smooth' });
  const handleVideoPlay = () => setVideoPlaying(true);
  const handleVideoKey  = e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleVideoPlay(); } };

  // Google Fonts — identical injection to CustomDietPlan.jsx
  useEffect(() => {
    const pc1   = Object.assign(document.createElement('link'), { rel:'preconnect', href:'https://fonts.googleapis.com' });
    const pc2   = Object.assign(document.createElement('link'), { rel:'preconnect', href:'https://fonts.gstatic.com', crossOrigin:'anonymous' });
    const sheet = Object.assign(document.createElement('link'), { rel:'stylesheet',
      href:'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,800;9..144,900&family=Poppins:wght@400;500;600;700&display=swap' });
    document.head.append(pc1, pc2, sheet);
    return () => { pc1.remove(); pc2.remove(); sheet.remove(); };
  }, []);

  // Per-page SEO meta — injected on mount, fully restored on unmount
  useEffect(() => {
    const PAGE_TITLE = 'Custom Diet Plan & Meal Plan | Neighborhood Boxing Club Columbus, OH';
    const PAGE_DESC  = 'Personalized meal plan built from your goals and favorite foods, with a weekly grocery list, cheat meal and text check-ins. $9.99/month for members, $19.99/month without a membership.';
    const PAGE_KW    = [
      'meal plan','custom meal plan','personalized meal plan','diet plan','custom diet plan',
      'personalized diet plan','weight loss meal plan','weight loss diet plan','healthy meal plan',
      'weekly meal plan','meal plan with grocery list','grocery list meal plan','macro meal plan',
      'calorie meal plan','high protein meal plan','meal plan for beginners','diet plan for boxers',
      'boxing diet plan','boxing meal plan','fitness meal plan','fat loss meal plan',
      'muscle building meal plan','diet plan by text','meal plan with cheat meal',
      'favorite foods meal plan','affordable meal plan','diet plan Columbus Ohio',
      'meal plan Columbus Ohio','nutrition plan Columbus','boxing gym Columbus Ohio',
      'boxing classes Columbus','boxing gym with diet plan',
    ].join(', ');

    const sel     = q  => document.querySelector(q);
    const getAttr = (el, a) => el?.getAttribute(a) ?? null;

    const origTitle   = document.title;
    const descEl      = sel('meta[name="description"]');
    const canonEl     = sel('link[rel="canonical"]');
    const ogTitleEl   = sel('meta[property="og:title"]');
    const ogDescEl    = sel('meta[property="og:description"]');
    const ogUrlEl     = sel('meta[property="og:url"]');
    const twTitleEl   = sel('meta[name="twitter:title"]');
    const twDescEl    = sel('meta[name="twitter:description"]');

    const origDesc    = getAttr(descEl,    'content');
    const origCanon   = getAttr(canonEl,   'href');
    const origOgTitle = getAttr(ogTitleEl, 'content');
    const origOgDesc  = getAttr(ogDescEl,  'content');
    const origOgUrl   = getAttr(ogUrlEl,   'content');
    const origTwTitle = getAttr(twTitleEl, 'content');
    const origTwDesc  = getAttr(twDescEl,  'content');

    document.title = PAGE_TITLE;
    descEl?.setAttribute('content', PAGE_DESC);
    canonEl?.setAttribute('href',   `${SITE}/custom-diet-plan`);
    ogTitleEl?.setAttribute('content', PAGE_TITLE);
    ogDescEl?.setAttribute('content',  PAGE_DESC);
    ogUrlEl?.setAttribute('content',   `${SITE}/custom-diet-plan`);
    twTitleEl?.setAttribute('content', PAGE_TITLE);
    twDescEl?.setAttribute('content',  PAGE_DESC);

    // Keywords meta (doesn't exist on homepage — inject, remove on unmount)
    const kwEl = Object.assign(document.createElement('meta'), { id: 'cdp-kw' });
    kwEl.setAttribute('name', 'keywords');
    kwEl.setAttribute('content', PAGE_KW);
    document.head.appendChild(kwEl);

    // JSON-LD — Service with Offer starting at $9.99/month for members
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Custom Diet Plan',
      description: 'A personalized weekly meal plan built around your fitness goals and favorite foods, with a weekly grocery list, cheat meal, and text check-ins from the gym.',
      provider: {
        '@type': 'LocalBusiness',
        name: 'Neighborhood Boxing Club',
        url: SITE,
        telephone: '+1-614-547-6096',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '4324 N High St',
          addressLocality: 'Columbus',
          addressRegion: 'OH',
          postalCode: '43214',
          addressCountry: 'US',
        },
      },
      areaServed: { '@type': 'City', name: 'Columbus, OH' },
      offers: {
        '@type': 'Offer',
        price: '9.99',
        priceCurrency: 'USD',
        description: 'Monthly subscription for active gym members, cancel anytime',
      },
      url: `${SITE}/custom-diet-plan`,
    };
    const ldScript = Object.assign(document.createElement('script'), {
      type: 'application/ld+json', id: 'cdp-ld',
      textContent: JSON.stringify(jsonLd),
    });
    document.head.appendChild(ldScript);

    return () => {
      document.title = origTitle;
      if (descEl    && origDesc    !== null) descEl.setAttribute('content',    origDesc);
      if (canonEl   && origCanon   !== null) canonEl.setAttribute('href',      origCanon);
      if (ogTitleEl && origOgTitle !== null) ogTitleEl.setAttribute('content', origOgTitle);
      if (ogDescEl  && origOgDesc  !== null) ogDescEl.setAttribute('content',  origOgDesc);
      if (ogUrlEl   && origOgUrl   !== null) ogUrlEl.setAttribute('content',   origOgUrl);
      if (twTitleEl && origTwTitle !== null) twTitleEl.setAttribute('content', origTwTitle);
      if (twDescEl  && origTwDesc  !== null) twDescEl.setAttribute('content',  origTwDesc);
      document.getElementById('cdp-kw')?.remove();
      document.getElementById('cdp-ld')?.remove();
    };
  }, []);

  return (
    <div className="dp-wrapper" style={{ background:C.white, fontFamily:POPPINS, color:C.ink, overflowX:'hidden', minHeight:'100vh' }}>
      <style>{RESPONSIVE_CSS}{PUBLIC_MOBILE_CSS}{`
        .dp-mobile-video { display:block; }
        .dp-hero-desktop-video { display:none; }
        @container dp-page (min-width:768px) {
          .dp-mobile-video { display:none; }
          .dp-hero-desktop-video { display:block; }
        }
      `}</style>

      {/* Mobile-only fixed white header (logo + Get Started button) */}
      <PublicMobileHeader onGetStarted={goGetStarted} />

      {/* Desktop-only sticky TopBar (hidden on mobile via PUBLIC_MOBILE_CSS) */}
      <div className="dp-topbar-hide-mobile">
        <TopBar scrollToPrice={scrollToPrice} />
      </div>

      {/* Sales content — exact copy of member page sections */}
      <HeroSection videoPlaying={videoPlaying} onVideoPlay={handleVideoPlay} onVideoKey={handleVideoKey} scrollToPrice={scrollToPrice} />
      <HowItWorks />
      <EatYourWaySection />
      <EverythingSection />
      <GymSection />
      <KitchenSection />
      <PublicPriceSection onGetStarted={goGetStarted} />

      {/* FAQ — identical to member page */}
      <section style={{ background:C.mint,padding:'64px 20px 80px',boxSizing:'border-box' }}>
        <div style={{ maxWidth:3*480,margin:'0 auto' }}>
          <h2 style={{ fontFamily:FRAUNCES,fontWeight:900,fontSize:'clamp(22px,4vw,32px)',color:C.ink,textAlign:'center',marginBottom:28 }}>
            Frequently Asked <span style={{ color:C.green }}>Questions</span>
          </h2>
          <dl style={{ display:'flex',flexDirection:'column',gap:0,maxWidth:680,margin:'0 auto' }}>
            {[
              { q:"Do I need to book before coming in?",        a:"Yes. All classes must be booked online in advance. Create your account at neighborhoodboxingclub.us and reserve your spot before you arrive. Walk-ins without a booking can't be admitted." },
              { q:"What's the best boxing gym in Columbus for beginners?", a:"Neighborhood Boxing Club on N High St. Your first class is free, there's a free Saturday beginners class, coaches teach technique from scratch, and we're rated 4.9★ on Google." },
              { q:"Are there boxing classes near Ohio State?",   a:"Yes. NBC is at 4324 N High St in Clintonville, just north of campus straight up High Street." },
              { q:"What's the schedule?",                       a:'Classes run 7 days a week. Weekdays have classes at 10:00 AM, 4:30 PM and 5:45 PM, plus 7:00 PM Mon/Tue/Thu. "Workout With Ron K" runs at 6:00 AM Mon/Wed/Fri and 7:00 PM Monday. Weekends have classes at 10:00 AM and 11:15 AM, plus the FREE Beginners Class Saturday at 12:30 PM. Times are the same every week.' },
              { q:"How much do classes cost?",                   a:"Drop-in is $19.99. Elite Unlimited is $79.99/month, and the Family Plan is $139.99/month. Your first class is free." },
              { q:"What's included in the Family Plan?",         a:"For $139.99/month, the Family Plan covers your immediate family: parents plus up to 3 kids. Larger families may be approved case by case, so just ask." },
              { q:"Do I need my own gear?",                     a:"You don't need your own gloves, because we lend free gloves for class and sell them when you're ready. You do need hand wraps. We sell them at the gym for $9.99 plus tax." },
              { q:"Do you offer boxing for kids?",              a:"Group classes are for ages 13+. Kids under 13 train one-on-one through private training. Text Ron at (614) 547-6096." },
              { q:"Is there a contract?",                       a:"No. All plans are month to month with no contract." },
              { q:"Is there parking?",                          a:"Yes, parking is available at the gym." },
            ].map((item,i) => (
              <div key={i} style={{ borderBottom:'1px solid rgba(0,0,0,0.08)' }}>
                <dt>
                  <button onClick={() => setFaqOpenIdx(faqOpenIdx===i?null:i)}
                    style={{ width:'100%',textAlign:'left',padding:'16px 0',display:'flex',alignItems:'center',
                      justifyContent:'space-between',gap:12,fontFamily:POPPINS,fontWeight:600,fontSize:14,
                      color:C.ink,background:'none',border:'none',cursor:'pointer',minHeight:52 }}
                    aria-expanded={faqOpenIdx===i}>
                    <span>{item.q}</span>
                    <span style={{ color:C.green,fontSize:18,flexShrink:0 }}>{faqOpenIdx===i?'−':'+'}</span>
                  </button>
                </dt>
                <dd style={{ maxHeight:faqOpenIdx===i?400:0,overflow:'hidden',transition:'max-height 0.3s ease',
                  fontFamily:POPPINS,fontSize:14,color:C.ink,lineHeight:1.6,paddingBottom:faqOpenIdx===i?16:0,margin:0 }}>
                  {item.a}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Footer — identical to member page */}
      <footer style={{ background:C.white,padding:'20px',textAlign:'center',borderTop:'1px solid #eee' }}>
        <AiDisclaimerSmall />
        <p style={{ fontFamily:POPPINS,fontSize:13,color:'#888',margin:'10px 0 0' }}>Cancel anytime: text the gym at (614) 547-6096</p>
      </footer>

      {/* Bottom spacer keeps footer above sticky bar on mobile */}
      <div className="dp-mobile-spacer" style={{ height: 80 }} aria-hidden="true" />

      {/* Mobile sticky CTA */}
      <PublicStickyBar onGetStarted={goGetStarted} />
    </div>
  );
}
